import { useState } from "react";
import {
  Search, Moon, Sun, ChevronLeft, AlertTriangle, ShieldAlert,
  CheckCircle2, XCircle, RotateCcw, Menu, X,
} from "lucide-react";

const QUEUE = [
  { id: 1, name: "Maria Alvarez", doc: "License", issue: "Name mismatch", time: "2h ago", severity: "soft" },
  { id: 2, name: "Tuan Nguyen", doc: "Insurance", issue: "Duplicate policy #", time: "4h ago", severity: "hard" },
  { id: 3, name: "Richard Osei", doc: "License", issue: "Forensics risk", time: "1d ago", severity: "hard" },
  { id: 4, name: "Sana Patel", doc: "Insurance", issue: "Named insured mismatch", time: "1d ago", severity: "soft" },
  { id: 5, name: "Kayla Williams", doc: "License", issue: "Number mismatch", time: "1d ago", severity: "hard" },
];

const RECORDS = {
  1: {
    submitted: "Submitted 2 hours ago",
    docType: "Driver License Verification",
    signals: [
      { text: "Name mismatch between front and back", score: "0.71 similarity", hard: false },
      { text: "Forensics risk score elevated", score: "0.82", hard: true },
    ],
    audit: [
      "12:04 PM — System flagged: forensics risk score 0.82",
      "12:04 PM — System flagged: name mismatch (0.71 similarity)",
      "11:58 AM — Driver submitted license front/back",
    ],
  },
};

function getRecord(id) {
  return RECORDS[id] || {
    submitted: "Submitted recently",
    docType: "Document Verification",
    signals: [{ text: "Flagged for manual review", score: "", hard: false }],
    audit: ["Awaiting dispatcher review"],
  };
}

export default function DispatcherDashboard() {
  const [dark, setDark] = useState(false);
  const [activeId, setActiveId] = useState(1);
  const [mobileQueueOpen, setMobileQueueOpen] = useState(false);

  const active = QUEUE.find((q) => q.id === activeId);
  const record = getRecord(activeId);

  // Theme tokens (Tailwind core utility classes only — no dark: variant reliance)
  const t = dark
    ? {
        page: "bg-slate-950 text-slate-100",
        topbar: "bg-slate-950 border-slate-800",
        surface: "bg-slate-900 border-slate-800",
        surfaceAlt: "bg-slate-800/60",
        border: "border-slate-800",
        text2: "text-slate-400",
        hover: "hover:bg-slate-800",
        activeItem: "bg-blue-500/10 border-blue-500",
        input: "bg-slate-800 border-slate-700 placeholder-slate-500 text-slate-100",
        chip: "bg-slate-800 border-slate-700",
      }
    : {
        page: "bg-slate-100 text-slate-900",
        topbar: "bg-slate-900 border-slate-800",
        surface: "bg-white border-slate-200",
        surfaceAlt: "bg-slate-50",
        border: "border-slate-200",
        text2: "text-slate-500",
        hover: "hover:bg-slate-50",
        activeItem: "bg-blue-50 border-blue-500",
        input: "bg-slate-800/10 border-slate-300 placeholder-slate-400 text-slate-900",
        chip: "bg-slate-100 border-slate-200",
      };

  const badgeClasses = dark
    ? "bg-amber-400/10 text-amber-400"
    : "bg-amber-100 text-amber-700";

  function selectDriver(id) {
    setActiveId(id);
    setMobileQueueOpen(false);
  }

  return (
    <div className={`min-h-screen w-full ${t.page} font-sans transition-colors`}>
      {/* Top bar */}
      <div className={`sticky top-0 z-20 flex items-center justify-between gap-3 border-b px-4 py-3 ${t.topbar}`}>
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="md:hidden shrink-0 rounded-lg p-2 bg-white/10 text-white"
            onClick={() => setMobileQueueOpen(true)}
            aria-label="Open flagged queue"
          >
            <Menu className="h-4 w-4" />
          </button>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-500 text-xs font-bold text-white">
            PL
          </div>
          <span className="truncate text-sm font-semibold text-white sm:text-base">
            Dispatch Control Center
          </span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative hidden md:block">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              className="w-56 rounded-lg border border-white/15 bg-white/10 py-2 pl-8 pr-3 text-xs text-white placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search drivers, VIN…"
            />
          </div>
          <button
            onClick={() => setDark((d) => !d)}
            className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2.5 py-2 text-xs font-semibold text-white"
          >
            {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{dark ? "Light mode" : "Dark mode"}</span>
          </button>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
            JD
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl">
        {/* Queue — desktop sidebar */}
        <aside className={`hidden md:block w-72 shrink-0 border-r ${t.border} ${t.surface} px-4 py-5`}>
          <QueueList
            queue={QUEUE}
            activeId={activeId}
            onSelect={selectDriver}
            t={t}
            dark={dark}
          />
        </aside>

        {/* Queue — mobile drawer */}
        {mobileQueueOpen && (
          <div className="fixed inset-0 z-30 md:hidden">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileQueueOpen(false)}
            />
            <div className={`absolute left-0 top-0 h-full w-80 max-w-[85%] overflow-y-auto border-r ${t.border} ${t.surface} px-4 py-5`}>
              <div className="mb-4 flex items-center justify-between">
                <span className={`text-xs font-semibold uppercase tracking-wide ${t.text2}`}>
                  Flagged Queue
                </span>
                <button onClick={() => setMobileQueueOpen(false)} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <QueueList
                queue={QUEUE}
                activeId={activeId}
                onSelect={selectDriver}
                t={t}
                dark={dark}
                hideHeading
              />
            </div>
          </div>
        )}

        {/* Main review panel */}
        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 sm:py-6">
          <button
            className={`mb-3 flex items-center gap-1 text-xs font-medium md:hidden ${t.text2}`}
            onClick={() => setMobileQueueOpen(true)}
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to queue
          </button>

          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold sm:text-xl">{active.name}</h1>
              <p className={`mt-0.5 text-xs sm:text-sm ${t.text2}`}>
                {record.docType} · {record.submitted}
              </p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${badgeClasses}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" /> Flagged
            </span>
          </div>

          {/* Document previews */}
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {["Front · OCR capture", "Back · Barcode capture"].map((label) => (
              <div key={label} className={`overflow-hidden rounded-xl border ${t.surface}`}>
                <div className={`border-b px-3 py-2 text-xs font-semibold ${t.border} ${t.text2}`}>
                  {label}
                </div>
                <div
                  className={`flex h-32 items-center justify-center text-xs sm:h-40 ${t.text2} ${t.surfaceAlt}`}
                >
                  Document image
                </div>
              </div>
            ))}
          </div>

          {/* Fraud signals */}
          <Panel title="Fraud Signals" t={t}>
            <div className="space-y-2">
              {record.signals.map((s, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2.5 rounded-lg border-l-4 px-3 py-2.5 text-xs sm:text-sm ${
                    s.hard
                      ? dark
                        ? "border-red-500 bg-red-500/10"
                        : "border-red-500 bg-red-50"
                      : dark
                      ? "border-amber-400 bg-amber-400/10"
                      : "border-amber-500 bg-amber-50"
                  }`}
                >
                  {s.hard ? (
                    <ShieldAlert className="h-4 w-4 shrink-0 text-red-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                  )}
                  <span className="flex-1">{s.text}</span>
                  {s.score && (
                    <span className="font-mono text-[11px] font-semibold">{s.score}</span>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          {/* Notes */}
          <Panel title="Dispatcher Notes" t={t}>
            <textarea
              placeholder="Add a note for the audit trail…"
              className={`min-h-[80px] w-full rounded-lg border px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm ${t.input}`}
            />
          </Panel>

          {/* Actions */}
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white sm:text-sm">
              <CheckCircle2 className="h-4 w-4" /> Approve → Manual Verified
            </button>
            <button className="flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-2.5 text-xs font-semibold text-white sm:text-sm">
              <XCircle className="h-4 w-4" /> Reject
            </button>
            <button
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-4 py-2.5 text-xs font-semibold sm:text-sm ${t.border}`}
            >
              <RotateCcw className="h-4 w-4" /> Request Re-upload
            </button>
          </div>

          {/* Audit log */}
          <Panel title="Audit Log" t={t}>
            <div className={`space-y-1.5 text-xs ${t.text2}`}>
              {record.audit.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </Panel>
        </main>
      </div>
    </div>
  );
}

function QueueList({ queue, activeId, onSelect, t, dark, hideHeading }) {
  return (
    <div>
      {!hideHeading && (
        <h2 className={`mb-3 text-xs font-semibold uppercase tracking-wide ${t.text2}`}>
          Flagged Queue · {queue.length}
        </h2>
      )}
      <div className="space-y-1">
        {queue.map((item) => {
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.id)}
              className={`flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                isActive ? t.activeItem : `border-transparent ${t.hover}`
              }`}
            >
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  item.severity === "hard" ? "bg-red-500" : "bg-amber-400"
                }`}
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{item.name}</span>
                <span className={`block truncate text-xs ${t.text2}`}>
                  {item.doc} · {item.issue} · {item.time}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Panel({ title, children, t }) {
  return (
    <div className={`mb-5 rounded-xl border p-4 ${t.surface}`}>
      <h3 className={`mb-3 text-xs font-bold uppercase tracking-wide ${t.text2}`}>{title}</h3>
      {children}
    </div>
  );
}
